import Button from 'tio-ui/components/button';
import { dropTask } from 'ember-concurrency';
import { Listbox } from 'tio-ui/components/listbox';
import Modal from 'tio-ui/components/modal';
import Footer from 'tio-ui/components/footer';
import { not, or, and } from 'tio-ui/utilities';
import {
  PhArrowLineDown,
  PhArrowLineRight,
  PhDotsThree,
  PhXCircle,
  PhArrowUUpLeft,
  PhHandDeposit,
  PhUserSwitch,
  PhClockCounterClockwise,
  PhShieldWarning,
  PhTag,
} from 'ember-phosphor-icons';
import type IntlService from 'ember-intl/services/intl';
import type SessionContextService from '../../../services/session-context.ts';
import Popover from 'tio-ui/components/popover';
import type { PopoverPlacement } from 'tio-ui/components/popover';
import type { TOC } from '@ember/component/template-only';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Textarea from 'tio-ui/components/textarea';
import { cached } from '@glimmer/tracking';
import { tracked } from '@glimmer/tracking';
import AlertBanner from 'tio-ui/components/alert-banner';
import ApproveModal from './approve-modal.gts';
import ReferenceControls from './reference-controls.gts';
import type { ReferenceDrawer } from './reference-controls.gts';
import type { ReferenceControlsPlacement } from '../../../services/tas-approval-layout.ts';
import Component from '@glimmer/component';
import SelectTextArea from '../../tio/select-text-area.gts';
import ErrorMessages from 'tio-ui/components/error-messages';
import {
  buildTemplateResponseOptions,
  getSingleValueForTasField,
  setTrackedTasFieldValue,
  type TemplateFieldName,
} from '../../../utils/tuition-assistance/fields.ts';
import { setSemesterCodeString } from '../../../utils/format.ts';
import {
  reloadRecord,
  saveRecord,
  saveExistingRecordWithChangelog,
} from 'tio-data/requests/save-record';
import { holdTasParticipantRequest } from 'tio-data/requests/tas-participant-actions';
import { concat } from '@ember/helper';
import Checkbox from 'tio-ui/components/checkbox';
import Input from 'tio-ui/components/input';
import SelectableCard from 'tio-ui/components/selectable-card';
import inputmask from '../../../modifiers/inputmask.ts';
import { formatAsDollars } from '../../../utils/format.ts';
import { getInputmaskUnmaskedValue } from '../../../utils/inputmask.ts';
import { rejectHoldNote } from '../../../utils/reject-hold-note.ts';
import parseApiError from '../../../utils/parse-api-error.ts';
import { canAccessPolicyOverride } from '../../../utils/tas/policy-override-access.ts';
import { isEmployeeFillableTemplateField } from '../../../utils/tuition-assistance/draft-submit-fields.ts';
import {
  buildTasTransitionFieldData,
  declineCoursesRequest,
  declineEvidenceRequest,
  loadTasApplicationHistory,
  rescindLastApproverActionRequest,
  requestRepaymentRequest,
  toggleTasApplicationInCollections,
  transitionTasApplicationRequest,
  updateApprovedTotalRequest,
  TAS_TRANSITION_EVENT_FOR_METHOD,
} from 'tio-data/requests/tas-application-actions';
import {
  requestChangesRequest as declineProgramRequest,
  requestProgramCompletionChangesRequest,
  requestProgramWithdrawalChangesRequest,
  rescindLastApproverActionRequest as rescindTasProgramInstanceRequest,
} from 'tio-data/requests/tas-program-instance';
import { buildTasActorFromSession } from 'tio-data/utils/tas-actor';
import type {
  ApplicationApprovalSummary,
  ApplicationApprovalSummaryApprover,
} from '../../../types/tuition-assistance';
import type FeaturesService from '../../../services/features';
import type TuitionAssistanceService from '../../../services/tuition-assistance.ts';
import type { Store as StoreService } from '@warp-drive/core';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { RoleViewExtension as RoleView } from 'tio-data/data/resources/role-view.ext';

interface MoreActionsMenuSignature {
  Element: HTMLButtonElement | HTMLAnchorElement;
  Args: {
    /** Where the menu opens relative to its trigger. */
    placement: PopoverPlacement;
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
    /** Disables the trigger, e.g. while Approve or Request Change is open. */
    isDisabled: boolean;
    onAction: (key: string) => void;
    disabledKeys: string[];
    isRejected: boolean;
    canSeePolicyOverride: boolean;
    showInCollections: boolean;
    isInCollections: boolean;
    /** Adds the item that moves the drawer buttons between the action bar and the rail. */
    canMoveReferenceControls: boolean;
    referenceControlsOnRight: boolean;
  };
}

/** The approval "…" menu. It lives with the drawer buttons, in the action bar or the right rail — never both. */
const MoreActionsMenu: TOC<MoreActionsMenuSignature> = <template>
  <Popover @placement={{@placement}} @isOpen={{@isOpen}} @onOpen={{@onOpen}} @onClose={{@onClose}}>
    <:trigger as |pop|>
      <Button
        @appearance="minimal"
        @intent="default"
        @size="sm"
        class="!p-2 !rounded-md hover:tio-bg-brand-soft transition-colors"
        @isDisabled={{@isDisabled}}
        {{pop.register}}
        {{on "click" @onOpen}}
        ...attributes
      >
        <PhDotsThree @size="24" class="tio-fg-secondary hover:tio-fg-brand" />
      </Button>
    </:trigger>

    <:body>
      <Listbox @intent="primary" @onAction={{@onAction}} @disabledKeys={{@disabledKeys}} as |l|>
        <l.Item @key="submitForUser" data-test-footer-submit-for-user-button>
          <span class="tio-row-tight">
            <PhHandDeposit @size="16" />
            Submit For User
          </span>
        </l.Item>

        <l.Separator />

        <l.Item @key="reassign" data-test-footer-reassign-button>
          <span class="tio-row-tight">
            <PhUserSwitch @size="16" />
            {{t "tuition_assistance.approval_queue.approval_status"}}
          </span>
        </l.Item>

        <l.Item @key="rescind" data-test-footer-rescind-button>
          <span class="tio-row-tight">
            <PhArrowUUpLeft @size="16" />
            Undo Last
          </span>
        </l.Item>

        <l.Separator />

        {{#if @isRejected}}
          <l.Item @key="reopen" @intent="primary" data-test-footer-reopen-button>
            <span class="tio-row-tight">
              <PhClockCounterClockwise @size="16" />
              Reopen Application
            </span>
          </l.Item>
        {{else}}
          <l.Item @key="reject" @intent="danger" data-test-footer-reject-button>
            <span class="tio-row-tight">
              <PhXCircle @size="16" />
              {{t "tuition_assistance.application_details.reject_application"}}
            </span>
          </l.Item>
        {{/if}}

        {{#if @canSeePolicyOverride}}
          <l.Separator />
          <l.Item
            @key="policyOverride"
            data-test-footer-policy-override-button
            class="hover:!tio-fg-danger hover:!tio-bg-danger-soft"
          >
            <span class="tio-row-tight">
              <PhShieldWarning @size="16" />
              {{t "common.tuition_assistance.application_details.policy_override_prompt"}}
            </span>
          </l.Item>
        {{/if}}

        {{#if @showInCollections}}
          <l.Separator />
          <l.Item @key="inCollections" data-test-footer-in-collections-button>
            <span class="tio-row-tight">
              <PhTag @size="16" />
              {{if
                @isInCollections
                (t "tuition_assistance.application_details.in_collections_toggle.remove_title")
                (t "tuition_assistance.application_details.in_collections_toggle.mark_title")
              }}
            </span>
          </l.Item>
        {{/if}}

        {{#if @canMoveReferenceControls}}
          <l.Separator />
          <l.Item @key="moveReferenceControls" data-test-footer-move-reference-controls-button>
            <span class="tio-row-tight">
              {{#if @referenceControlsOnRight}}
                <PhArrowLineDown @size="16" />
                {{t "common.tas.approval.reference_controls.move_to_bottom"}}
              {{else}}
                <PhArrowLineRight @size="16" />
                {{t "common.tas.approval.reference_controls.move_to_right"}}
              {{/if}}
            </span>
          </l.Item>
        {{/if}}
      </Listbox>
    </:body>
  </Popover>
</template>;

interface ApprovalFooterSignature {
  Element: HTMLDivElement;
  Args: {
    /** Opens a page drawer that has no button of its own, such as `reassign-approver`. */
    onDrawerOpen: (drawerType: string) => void;
    /** Opens a reference drawer, or closes it when it is already the open one. */
    onReferenceToggle: (drawer: ReferenceDrawer) => void;
    /** Highlights the matching reference control; null or omission leaves all unselected. */
    activeDrawer?: string | null;
    /** Shows approval progress and disables the Approve button while true. */
    approveButtonLoading?: boolean;
    /** Live application whose state controls available actions; actions may save and reload this record. */
    application: TasApplicationModel;
    /** Defaults to the app identified by FeaturesService. */
    consumingApp?: string;
    /** Disables approval while the application has unsaved edits. */
    isEditingApplication?: boolean;
    /** Query param to enable AI analysis in production (tio-admin only) */
    ai?: boolean;
    /** Label for the expenses reference control; an empty or omitted value uses the translated Courses label. */
    expenseType?: string;
    /** Shows the admin-only CourseCash transactions button. */
    hasCourseCash?: boolean;
    /**
     * Opens Approve and Request Change without blocking the page, so reference drawers stay
     * usable while the approver fills them in.
     */
    nonModalDecisions?: boolean;
    /** Called when Approve or Request Change opens (`true`) or closes (`false`). */
    onDecisionFormChange?: (isOpen: boolean) => void;
    /** Where the drawer buttons live; `right` moves them and the "…" menu out of this bar at `md` and up. */
    referenceControls?: ReferenceControlsPlacement;
    /**
     * Offers "move drawer buttons" in the "…" menu and renders the right-edge rail they move to;
     * omit to hide both, as an embedded page does.
     */
    onMoveReferenceControls?: () => void;
  };
}

class ApprovalFooter extends Component<ApprovalFooterSignature> {
  @service declare store: StoreService;
  @service declare features: FeaturesService;
  @service declare intl: IntlService;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare sessionContext: SessionContextService;

  @tracked isRejectModalOpen = false;
  @tracked rejectNote = '';
  @tracked rejectError: string | null = null;

  // Seed repayment defaults when opening: amounts may still be loading at construction, and the API rejects zero.
  @tracked requestRepaymentOnReject = false;
  @tracked holdEmployeeOnReject = false;
  @tracked requestedRepaymentAmount = 0;
  @tracked requestedRepaymentAmountDisplay = '';
  @tracked repaymentAmountSelection = 'full';

  @tracked isApproveModalOpen = false;
  @tracked approveCommentDraft = '';

  @tracked submitForUserModalOpen = false;
  @tracked submitForUserError: string | null = null;

  @tracked isRescindModalOpen = false;
  @tracked rescindError: string | null = null;

  @tracked isRequestChangeModalOpen = false;
  @tracked requestChangeNote = '';
  @tracked requestChangeError: string | null = null;

  @tracked selectedApproverToReassign: ApplicationApprovalSummaryApprover | null = null;
  @tracked isReassignModalOpen = false;

  @tracked isReopenModalOpen = false;
  @tracked reopenError: string | null = null;

  @tracked isOverrideModalOpen = false;

  @tracked isInCollectionsModalOpen = false;
  @tracked inCollectionsError: string | null = null;

  @tracked isMoreActionsPopoverOpen = false;

  get computedConsumingApp(): string {
    if (this.args.consumingApp) {
      return this.args.consumingApp;
    }

    if (this.features.isAdminApp) {
      return 'tio-admin';
    } else if (this.features.isEmployeeApp) {
      return 'tio-employee';
    } else if (this.features.isAgentApp) {
      return 'tio-agent';
    }

    return 'unknown';
  }

  get approvalQueue(): ApplicationApprovalSummary {
    const { application } = this.args;

    if (application.state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL') {
      return application.evidenceApproval;
    } else if (application.state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL') {
      return application.coursesApproval;
    } else {
      return {
        affirmed: [],
        current_reviewers: [],
        possible_reviewers: [],
        required: [],
        approved: false,
      };
    }
  }

  get canReassignApprover(): boolean {
    const approvalQueue = this.approvalQueue;
    return approvalQueue.possible_reviewers?.length > 0;
  }

  get companyId(): string {
    return this.args.application.tasProgramInstance.employee.company.id ?? '';
  }

  get canRejectApplication() {
    const state = this.args.application.state;
    return (
      state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL' ||
      state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL'
    );
  }

  get canRequestChange() {
    return this.canRejectApplication; // Same logic as reject
  }

  get canApproveApplication() {
    if (this.args.isEditingApplication) {
      return false;
    }

    const state = this.args.application.state;
    return state.includes('PENDING');
  }

  get requestChangeAdapterMethod(): string {
    const state = this.args.application.state;

    if (state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL') {
      return 'requestChanges';
    } else if (state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL') {
      return 'requestCourseEvidenceChanges';
    }

    return 'requestChanges'; // Default fallback
  }

  private getFieldNameForAction(action: 'reject' | 'requestChange'): TemplateFieldName | null {
    const state = this.args.application.state;

    if (state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL') {
      return action === 'reject'
        ? ('EVIDENCE_APPROVAL_REJECT_TEXT' as TemplateFieldName)
        : ('EVIDENCE_APPROVAL_REVISE_TEXT' as TemplateFieldName);
    }

    if (state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL') {
      return action === 'reject'
        ? ('COURSES_APPROVAL_REJECT_TEXT' as TemplateFieldName)
        : ('COURSES_APPROVAL_REVISE_TEXT' as TemplateFieldName);
    }

    return null;
  }

  private getTagForAction(action: 'reject' | 'requestChange'): string | null {
    const state = this.args.application.state;

    if (state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL') {
      return action === 'reject' ? 'TAS.EvidenceApproval.REJECT' : 'TAS.EvidenceApproval.REVISE';
    }

    if (state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL') {
      return action === 'reject' ? 'TAS.CourseApproval.REJECT' : 'TAS.CourseApproval.REVISE';
    }

    return null;
  }

  private companyCommentsForTag(tag: string): { title: string; body: string }[] {
    const partnerComments =
      this.args.application.tasProgramInstance?.employee?.company?.comments ?? [];
    return partnerComments
      .filter((comment) => comment.tags?.includes(tag))
      .map((comment) => comment.tasApprovalNote);
  }

  // Prefill and dropdown use the same merged program and company responses so neither source hides the other.
  private combinedResponseOptions(
    action: 'reject' | 'requestChange'
  ): { title: string; body: string }[] {
    const template = this.args.application.tasProgramInstance?.tasProgramTemplate;
    const templateOptions = buildTemplateResponseOptions(
      this.getFieldNameForAction(action),
      template?.fields,
      template?.tasFieldOptions ?? [],
      this.intl.t('common.tas.approval.program_response_label')
    );
    const tag = this.getTagForAction(action);
    const companyOptions = tag ? this.companyCommentsForTag(tag) : [];

    return [...templateOptions, ...companyOptions];
  }

  get templateRejectComment() {
    const allOptions = this.combinedResponseOptions('reject');
    return allOptions.length === 1 ? (allOptions[0]?.body ?? '') : '';
  }

  get templateRequestChangeComment() {
    const allOptions = this.combinedResponseOptions('requestChange');
    return allOptions.length === 1 ? (allOptions[0]?.body ?? '') : '';
  }

  /** Multiple reject responses render a dropdown; a lone response prefills the plain textarea. */
  get rejectTextAreaOptions() {
    const allOptions = this.combinedResponseOptions('reject');
    return allOptions.length > 1 ? allOptions : [];
  }

  /**
   * Multiple request-change responses render a dropdown; a lone response prefills the plain
   * textarea.
   */
  get requestChangeTextAreaOptions() {
    const allOptions = this.combinedResponseOptions('requestChange');
    return allOptions.length > 1 ? allOptions : [];
  }

  get isCourseCashPayment() {
    return this.args.application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
  }

  /**
   * Whether the employee has completed their cardholder profile (accepted T&C + DOB)
   */
  get employeeHasCardholder() {
    return (this.args.application.tasProgramInstance?.employee?.pcsCardHolders?.length ?? 0) > 0;
  }

  /** Missing requirements appear in the modal instead of disabling this menu action. */
  get canSubmitForUser() {
    const state = this.args.application.state;

    const allowedStates = ['TAS.ApplicationState.ATTEND', 'TAS.ApplicationState.DEFINE_COURSES'];

    return allowedStates.includes(state) && this.hasApprovalPermissions;
  }

  /**
   * Whether submit-for-user should be blocked for DRAFT course cash apps
   * without a cardholder profile. The employee must accept T&C and enter DOB first.
   */
  get isDraftCourseCashWithoutCardholder() {
    return (
      this.args.application.state === 'TAS.ApplicationState.DEFINE_COURSES' &&
      this.isCourseCashPayment &&
      !this.employeeHasCardholder
    );
  }

  get requiresGradePointAverageForSubmitForUser() {
    const application = this.args.application;
    const template = application.tasProgramInstance?.tasProgramTemplate;
    const isTypeTwoEvidence =
      application.typeClassification === 'TAS.ProgramType.2' ||
      application.typeClassification === 'TAS.ProgramType.4.2';
    const isTypeThreeEvidenceInAttend =
      (application.typeClassification === 'TAS.ProgramType.3' ||
        application.typeClassification === 'TAS.ProgramType.4.3') &&
      application.state === 'TAS.ApplicationState.ATTEND';

    return Boolean(
      template?.calculateTotalByGPA && (isTypeTwoEvidence || isTypeThreeEvidenceInAttend)
    );
  }

  get hasGradePointAverageForSubmitForUser() {
    const gradePointAverage = getSingleValueForTasField(
      'GRADE_POINT_AVERAGE',
      this.args.application.fields
    );
    return Number(gradePointAverage) > 0;
  }

  get submitForUserAdapterMethod(): string {
    const state = this.args.application.state;

    if (state === 'TAS.ApplicationState.ATTEND') {
      return 'requestCourseEvidence';
    }

    const typeClassification = this.args.application.typeClassification;
    const actionMap: Record<string, string> = {
      'TAS.ProgramType.2': 'requestCourseEvidence',
      'TAS.ProgramType.3': 'requestCoursesApproval',
      'TAS.ProgramType.4.2': 'requestCourseEvidence',
      'TAS.ProgramType.4.3': 'requestCoursesApproval',
    };

    return actionMap[typeClassification] || 'requestCourseEvidence';
  }

  get hasApprovalPermissions(): boolean {
    const user = this.sessionContext.user;

    return Boolean(
      user?.isEmployeeAdmin ||
      user?.isAccountOwner ||
      user?.isTioAdmin ||
      user?.isTasTioApprover ||
      (this.args.application.isPrepaidProgram && user?.isPayOps)
    );
  }

  /**
   * Policy override is limited to TIO admins/approvers or company owners/employee admins, excluding
   * the approver ladder.
   */
  get canSeePolicyOverride() {
    return canAccessPolicyOverride({
      isAdminApp: this.features.isAdminApp,
      isEmployeeApp: this.features.isEmployeeApp,
      roles: this.sessionContext.user?.roles,
    });
  }

  /** Policy override is one-way; already overridden applications have no second action. */
  get canOverridePolicies() {
    return this.canSeePolicyOverride && !this.args.application.ignorePolicies;
  }

  get showInCollections() {
    const companySetting =
      this.args.application.tasProgramInstance?.employee?.company?.companySetting;
    return companySetting?.tuitionAssistance?.isInCollectionsEnabled === true;
  }

  get isInCollections() {
    return this.args.application.isInCollections;
  }

  get inRescindableState() {
    const alwaysRescindableStates = new Set([
      'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL',
      'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL',
      'TAS.ProgramInstanceState.PENDING_WITHDRAWAL_APPROVAL',
      'TAS.ProgramInstanceState.PRIMED',
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
    ]);

    const state = this.args.application.state;
    const isPaid = this.args.application.isPaid;

    if (alwaysRescindableStates.has(state)) {
      return true;
    }

    if (state === 'TAS.ApplicationState.ATTEND' || state === 'TAS.ApplicationState.FULFILLED') {
      return !isPaid;
    }

    return false;
  }

  /**
   * Rescinding requires the previous approver role, except for TIO admins and company
   * owners/employee admins.
   */
  get userCanRescindApp() {
    const user = this.sessionContext.user;

    if (user?.isTioAdmin) {
      const userRoles: readonly RoleView[] = user?.uniqueRolesByRelationshipType || [];
      if (userRoles.some((role) => role.relationshipType === 'TAS.Admin.SUPERUSER')) {
        return true;
      }
    }

    if (user?.isAccountOwnerOrEmployeeAdmin) {
      return true;
    }

    const transitionLog = this.args.application.transitionLog || [];
    const currentState = this.args.application.state;

    // Match only the current approval stage, not earlier course approvals.
    const isEvidenceStage =
      currentState.includes('EVIDENCE') || currentState === 'TAS.ApplicationState.FULFILLED';
    const isCoursesStage =
      currentState.includes('COURSES') || currentState === 'TAS.ApplicationState.ATTEND';

    const relevantEvents = isEvidenceStage
      ? ['request_evidence_approval', 'approve_evidence', 'decline_evidence', 'reject_evidence']
      : isCoursesStage
        ? ['request_courses_approval', 'approve_courses', 'decline_courses', 'reject_courses']
        : [];

    // Auto-approval transitions have empty reason objects, so skip them when finding an approver.
    let transitionWithApprover;
    for (let i = transitionLog.length - 1; i >= 0; i--) {
      const transition = transitionLog[i];
      if (
        transition?.reason?.by?.actingWithRole?.relationshipType &&
        (relevantEvents.length === 0 || relevantEvents.includes(transition.event))
      ) {
        transitionWithApprover = transition;
        break;
      }
    }

    if (!transitionWithApprover) {
      return false; // No approver found, can't determine permission
    }

    const approverRoleType = transitionWithApprover.reason!.by!.actingWithRole.relationshipType;

    if (user?.isTioAdmin) {
      const userRoles: readonly RoleView[] = user?.uniqueRolesByRelationshipType || [];
      return userRoles.some((role) => role.relationshipType === approverRoleType);
    }

    // Match every role held here; the actor builder may choose a TAS role other than the session current role.
    const heldHere = this.sessionContext.rolesForCurrentCompany ?? [];
    return heldHere.some((role) => role.relationshipType === approverRoleType);
  }

  get showRescindButton() {
    return this.inRescindableState && this.userCanRescindApp;
  }

  get isRejected(): boolean {
    const rejectedStates = [
      'TAS.ProgramInstanceState.PROGRAM_REJECTED',
      'TAS.ProgramInstanceState.COMPLETION_REJECTED',
      'TAS.ProgramInstanceState.WITHDRAWAL_REJECTED',
      'TAS.ApplicationState.COURSES_REJECTED',
      'TAS.ApplicationState.EVIDENCE_REJECTED',
      'TAS.ApplicationState.REJECTED_PER_POLICY',
    ];

    return rejectedStates.includes(this.args.application.state);
  }

  get setAdapter(): string | undefined {
    const splitState = this.args.application.state.split('.').slice(0, 2).join('.');
    return splitState === 'TAS.ProgramInstanceState'
      ? 'tasProgramInstance'
      : splitState === 'TAS.ApplicationState'
        ? 'tasApplication'
        : undefined;
  }

  get adapterMethod(): string {
    const actionMap: Record<string, string> = {
      'TAS.ProgramInstanceState.PROGRAM_REJECTED': 'requestChanges',
      'TAS.ProgramInstanceState.COMPLETION_REJECTED': 'requestProgramCompletionChanges',
      'TAS.ProgramInstanceState.WITHDRAWAL_REJECTED': 'requestProgramWithdrawalChanges',
      'TAS.ApplicationState.COURSES_REJECTED': 'requestChanges',
      'TAS.ApplicationState.EVIDENCE_REJECTED': 'requestCourseEvidenceChanges',
      'TAS.ApplicationState.REJECTED_PER_POLICY': 'rescindLastApproverAction',
    };
    return actionMap[this.args.application.state] || '';
  }

  get lastTransitionLogAutoRejectedFromAttend(): boolean {
    const log = this.args.application.transitionLog;
    const lastTransition = log?.at(-1);
    return (
      log?.length >= 2 &&
      lastTransition?.to === 'TAS.ApplicationState.EVIDENCE_REJECTED' &&
      lastTransition.from === 'TAS.ApplicationState.ATTEND'
    );
  }

  get lastApprovedTotalBeforeRejection(): number | undefined {
    return this.args.application.approvedTotalLog?.at(-1)?.from;
  }

  /**
   * Missing evidence requirements in ATTEND; missing type-specific preapproval or evidence
   * requirements in DRAFT.
   */
  get missingRequirementsForSubmitForUser(): string[] {
    const application = this.args.application;
    const state = application.state;

    if (state === 'TAS.ApplicationState.DEFINE_COURSES') {
      return this.missingRequirementsForDraftSubmit;
    }

    const { tasProgramInstance, isMissingCourseGrades, isMissingRequiredEvidence } = application;

    let missingRecs: string[] = [];
    const courseGradeRequired = tasProgramInstance.tasProgramTemplate.courseGradeRequired;

    if (courseGradeRequired && isMissingCourseGrades) {
      missingRecs = [...missingRecs, ' course grade(s)'];
    }

    if (isMissingRequiredEvidence) {
      missingRecs = [...missingRecs, ' evidence attachment'];
    }

    if (
      this.requiresGradePointAverageForSubmitForUser &&
      !this.hasGradePointAverageForSubmitForUser
    ) {
      missingRecs = [...missingRecs, ' GPA'];
    }

    return missingRecs;
  }

  @cached
  get missingRequirementsForDraftSubmit(): string[] {
    const application = this.args.application;
    const typeClassification = application.typeClassification;
    const template = application.tasProgramInstance?.tasProgramTemplate;
    let missingRecs: string[] = [];

    if (!application.activeCourses?.length) {
      missingRecs = [...missingRecs, ' at least one course'];
    }

    if (template?.fields) {
      const templateFields = template.fields as Record<
        string,
        {
          province?: string;
          required?: boolean;
          visible?: boolean;
          label?: string;
          values?: unknown[];
        }
      >;

      for (const [fieldName, config] of Object.entries(templateFields)) {
        if (!isEmployeeFillableTemplateField(fieldName, config)) {
          continue;
        }

        const province = config.province;

        if (province === 'APPLICATION') {
          const value = getSingleValueForTasField(
            fieldName as TemplateFieldName,
            application.fields
          );
          if (value === null || value === undefined || value === '') {
            missingRecs = [...missingRecs, ` ${config.label || fieldName}`];
          }
        }

        // PROGRAM employee-fillable fields live on the program instance alongside APPLICANT fields.
        if (province === 'APPLICANT' || province === 'PROGRAM') {
          const instanceFields = application.tasProgramInstance?.fields;
          const value = getSingleValueForTasField(fieldName as TemplateFieldName, instanceFields);
          if (value === null || value === undefined || value === '') {
            missingRecs = [...missingRecs, ` ${config.label || fieldName}`];
          }
        }
      }
    }

    if (template?.fields && application.activeCourses?.length) {
      const templateFields = template.fields as Record<
        string,
        {
          province?: string;
          required?: boolean;
          visible?: boolean;
          label?: string;
          values?: unknown[];
        }
      >;

      const requiredCourseFields = Object.entries(templateFields).filter(
        ([fieldName, config]) =>
          config?.province === 'COURSE' && isEmployeeFillableTemplateField(fieldName, config)
      );

      for (const course of application.activeCourses) {
        const courseName =
          getSingleValueForTasField('COURSE_NAME', course.fields) || 'Unnamed course';

        for (const [fieldName, config] of requiredCourseFields) {
          const value = getSingleValueForTasField(fieldName as TemplateFieldName, course.fields);
          if (value === null || value === undefined || value === '') {
            missingRecs = [...missingRecs, ` ${config.label || fieldName} (${courseName})`];
          }
        }

        const courseCustomFields = course.customFields || [];
        const missingCourseCustom = courseCustomFields.filter(
          (f) =>
            f.required &&
            f.visible &&
            (!f.values?.length ||
              f.values[0] === undefined ||
              f.values[0] === null ||
              f.values[0] === '')
        );
        if (missingCourseCustom.length) {
          missingRecs = [
            ...missingRecs,
            ...missingCourseCustom.map((f) => ` ${f.label} (${courseName})`),
          ];
        }
      }
    }

    const appCustomFields = application.customFields || [];
    const missingCustomFields = appCustomFields.filter(
      (f) =>
        f.required &&
        f.visible &&
        (!f.values?.length ||
          f.values[0] === undefined ||
          f.values[0] === null ||
          f.values[0] === '')
    );
    if (missingCustomFields.length) {
      missingRecs = [...missingRecs, ...missingCustomFields.map((f) => ` ${f.label}`)];
    }

    const instanceCustomFields = application.tasProgramInstance?.customFields || [];
    const missingInstanceCustomFields = instanceCustomFields.filter(
      (f) =>
        f.required &&
        f.visible &&
        (!f.values?.length ||
          f.values[0] === undefined ||
          f.values[0] === null ||
          f.values[0] === '')
    );
    if (missingInstanceCustomFields.length) {
      missingRecs = [...missingRecs, ...missingInstanceCustomFields.map((f) => ` ${f.label}`)];
    }

    switch (typeClassification) {
      case 'TAS.ProgramType.2':
      case 'TAS.ProgramType.4.2': {
        const hasAttachments =
          application.hasRequiredEvidenceAttachments ||
          application.hasRequiredPreApprovalAttachments;
        if (!hasAttachments) {
          missingRecs = [...missingRecs, ' required attachment(s)'];
        }
        if (
          this.requiresGradePointAverageForSubmitForUser &&
          !this.hasGradePointAverageForSubmitForUser
        ) {
          missingRecs = [...missingRecs, ' GPA'];
        }
        break;
      }
      case 'TAS.ProgramType.3':
      case 'TAS.ProgramType.4.3':
        if (!application.hasRequiredPreApprovalAttachments) {
          missingRecs = [...missingRecs, ' required attachment(s)'];
        }
        break;
      default:
        break;
    }

    return missingRecs;
  }

  get missingRecsAlertForSubmitForUser(): string {
    return `You are missing the following requirement(s):${this.missingRequirementsForSubmitForUser}`;
  }

  get disabledMenuKeys(): string[] {
    const disabled: string[] = [];
    if (!this.canRejectApplication) {
      disabled.push('reject');
    }
    if (!this.isRejected) {
      disabled.push('reopen');
    }
    if (!this.canRequestChange) {
      disabled.push('requestChange');
    }
    if (!this.canSubmitForUser) {
      disabled.push('submitForUser');
    }
    if (!this.showRescindButton) {
      disabled.push('rescind');
    }
    if (!this.canReassignApprover) {
      disabled.push('reassign');
    }
    if (!this.canOverridePolicies) {
      disabled.push('policyOverride');
    }
    return disabled;
  }

  get rejectionCall() {
    return this.args.application.state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL'
      ? 'rejectCourseEvidence'
      : 'rejectCourses';
  }

  /**
   * Repayment shortcuts include only funded components; mixed funding offers their combined total
   * too.
   */
  @cached
  get repaymentAmountOptions() {
    const amounts = this.args.application.amounts;

    const prefix = 'tuition_assistance.application_details.reject_amount_option';

    const options = [
      {
        key: 'full',
        label: this.intl.t(`${prefix}.full`),
        description: this.intl.t(`${prefix}.full_description`),
        amount: Math.abs(amounts?.refund_limit ?? 0),
      },
      {
        key: 'reimbursement',
        label: this.intl.t(`${prefix}.reimbursement`),
        description: this.intl.t(`${prefix}.reimbursement_description`),
        amount: Math.abs(amounts?.paid ?? 0),
      },
      {
        key: 'courseCash',
        // SelectableCard treats labels as text, so use the plain brand variant instead of HTML markup.
        label: this.intl.t(`${prefix}.course_cash`, {
          courseCash: this.intl.t('common.tas.course_cash.label'),
        }),
        description: this.intl.t(`${prefix}.course_cash_description`),
        amount: Math.abs(amounts?.course_cash?.used ?? 0),
      },
    ].filter((option) => option.amount > 0);

    // `full` is the sum of the other two, so a single funded component makes it a duplicate card.
    const components = options.filter((option) => option.key !== 'full');
    const full = options.find((option) => option.key === 'full');

    return full && components.some((option) => option.amount === full.amount)
      ? components
      : options;
  }

  get showRepaymentAmountOptions() {
    return this.repaymentAmountOptions.length > 0;
  }

  get maxRepaymentAmount() {
    return Math.abs(this.args.application.amounts?.refund_limit ?? 0);
  }

  get isRejectFormValid() {
    if (!this.rejectNote) {
      return false;
    }
    // The repayment API rejects zero after rejection has already committed.
    return !this.requestRepaymentOnReject || this.requestedRepaymentAmount > 0;
  }

  #seedRepaymentAmount(amountCents: number) {
    this.requestedRepaymentAmount = amountCents;
    this.requestedRepaymentAmountDisplay = amountCents ? String(amountCents / 100) : '';
  }

  /**
   * Only ACTIVE participants can be held. Preserve existing hold notes so reopening cannot release
   * an unrelated hold.
   */
  get canHoldOnReject() {
    return Boolean(this.args.application.tasProgramInstance?.employee?.tasParticipant?.isActive);
  }

  openRejectModal = () => {
    if (!this.rejectNote) {
      this.rejectNote = this.templateRejectComment;
    }
    this.rejectError = null;
    this.#rejectionCommitted = false;
    this.#repaymentRequested = false;

    const eligible = this.args.application.canRepayOnReject;
    this.requestRepaymentOnReject = eligible;
    this.holdEmployeeOnReject = eligible && this.canHoldOnReject;
    const seeded = eligible ? Math.abs(this.args.application.amounts?.refund_limit ?? 0) : 0;
    // `full` is dropped when it duplicates a component, so select whichever card holds the seed.
    this.repaymentAmountSelection =
      this.repaymentAmountOptions.find((option) => option.amount === seeded)?.key ?? 'full';
    this.#seedRepaymentAmount(seeded);

    this.isRejectModalOpen = true;
  };

  closeRejectModal = () => {
    this.isRejectModalOpen = false;
    this.rejectError = null;
  };

  toggleRequestRepaymentOnReject = () => {
    this.requestRepaymentOnReject = !this.requestRepaymentOnReject;
  };

  toggleHoldEmployeeOnReject = () => {
    this.holdEmployeeOnReject = !this.holdEmployeeOnReject;
  };

  selectRepaymentAmount = (key: string) => {
    this.repaymentAmountSelection = key;

    const option = this.repaymentAmountOptions.find((opt) => opt.key === key);
    if (option) {
      this.#seedRepaymentAmount(option.amount);
    }
  };

  updateRequestedRepaymentAmount = (_maskedValue: unknown, event?: InputEvent) => {
    const dollars = Number(getInputmaskUnmaskedValue(event));
    const cents = Number.isFinite(dollars) ? Math.round(dollars * 100) : 0;

    // The API rejects amounts above the available balance after rejection has already committed.
    this.requestedRepaymentAmount = Math.min(cents, this.maxRepaymentAmount);

    // Leave the display value alone: rewriting it on each keystroke remounts the input and steals focus.
    this.repaymentAmountSelection = 'custom';
  };

  updateRejectNote = (value: string) => {
    this.rejectNote = value;
  };

  openSubmitForUserModal = () => {
    this.submitForUserModalOpen = true;
    this.submitForUserError = null;
  };

  closeSubmitForUserModal = () => {
    this.submitForUserModalOpen = false;
    this.submitForUserError = null;
  };

  openRescindModal = () => {
    this.isRescindModalOpen = true;
    this.rescindError = null;
  };

  closeRescindModal = () => {
    this.isRescindModalOpen = false;
    this.rescindError = null;
  };

  get isDecisionFormOpen() {
    return this.isApproveModalOpen || this.isRequestChangeModalOpen;
  }

  get referenceControlsOnRight() {
    return this.args.referenceControls === 'right';
  }

  get canMoveReferenceControls() {
    return Boolean(this.args.onMoveReferenceControls);
  }

  openApproveModal = () => {
    this.isApproveModalOpen = true;
    this.args.onDecisionFormChange?.(true);
  };

  closeApproveModal = () => {
    this.isApproveModalOpen = false;
    this.args.onDecisionFormChange?.(false);
  };

  updateApproveCommentDraft = (value: string) => {
    this.approveCommentDraft = value;
  };

  openRequestChangeModal = () => {
    this.isRequestChangeModalOpen = true;
    if (!this.requestChangeNote) {
      this.requestChangeNote = this.templateRequestChangeComment;
    }
    this.requestChangeError = null;
    this.args.onDecisionFormChange?.(true);
  };

  closeRequestChangeModal = () => {
    this.isRequestChangeModalOpen = false;
    this.requestChangeError = null;
    this.args.onDecisionFormChange?.(false);
  };

  updateRequestChangeNote = (value: string) => {
    this.requestChangeNote = value;
  };

  openReassignDrawer = () => {
    this.args.onDrawerOpen('reassign-approver');
  };

  handleReassignClick = (approver: ApplicationApprovalSummaryApprover) => {
    this.selectedApproverToReassign = approver;
    this.isReassignModalOpen = true;
  };

  closeReassignModal = () => {
    this.isReassignModalOpen = false;
    this.selectedApproverToReassign = null;
  };

  openReopenModal = () => {
    this.isReopenModalOpen = true;
    this.reopenError = null;
  };

  closeReopenModal = () => {
    this.isReopenModalOpen = false;
    this.reopenError = null;
  };

  openOverrideModal = () => {
    // Recheck permission here because the override is irreversible regardless of how the modal was opened.
    if (!this.canOverridePolicies) {
      return;
    }
    this.isOverrideModalOpen = true;
  };

  closeOverrideModal = () => {
    this.isOverrideModalOpen = false;
  };

  openInCollectionsModal = () => {
    this.isInCollectionsModalOpen = true;
    this.inCollectionsError = null;
  };

  closeInCollectionsModal = () => {
    this.isInCollectionsModalOpen = false;
    this.inCollectionsError = null;
  };

  toggleInCollectionsTask = dropTask(async () => {
    this.inCollectionsError = null;

    try {
      await toggleTasApplicationInCollections(this.store, this.args.application);
      await reloadRecord(this.store, this.args.application);
      this.closeInCollectionsModal();
    } catch (e) {
      console.error('Error toggling In Collections:', e);
      this.inCollectionsError = e instanceof Error ? e.message : String(e);
    }
  });

  confirmPolicyOverrideTask = dropTask(async () => {
    const today = this.intl.formatDate(new Date(), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    });

    const changelogEntry = `Ignore Policies set by ${this.sessionContext.user.person.fullName} on ${today}`;
    this.args.application.ignorePolicies = true;

    try {
      await saveExistingRecordWithChangelog(this.store, this.args.application, changelogEntry);
      this.isOverrideModalOpen = false;
    } catch {
      this.args.application.ignorePolicies = false;
    }
  });

  openMoreActionsPopover = () => {
    this.isMoreActionsPopoverOpen = true;
  };

  closeMoreActionsPopover = () => {
    this.isMoreActionsPopoverOpen = false;
  };

  handleMoreActionsMenu = (key: string) => {
    this.closeMoreActionsPopover();

    switch (key) {
      case 'reject':
        this.openRejectModal();
        break;
      case 'reopen':
        this.openReopenModal();
        break;
      case 'requestChange':
        this.openRequestChangeModal();
        break;
      case 'submitForUser':
        this.openSubmitForUserModal();
        break;
      case 'rescind':
        this.openRescindModal();
        break;
      case 'reassign':
        this.openReassignDrawer();
        break;
      case 'policyOverride':
        this.openOverrideModal();
        break;
      case 'inCollections':
        this.openInCollectionsModal();
        break;
      case 'moveReferenceControls':
        this.args.onMoveReferenceControls?.();
        break;
    }
  };

  /**
   * Mirrors the employee submit flow (prepareTotalsMiscFieldsAndSave + validateLimits)
   * for DRAFT (DEFINE_COURSES) state submissions on behalf of the user.
   */
  private async prepareDraftSubmission(application: TasApplicationModel) {
    const template = application.tasProgramInstance?.tasProgramTemplate;
    if (!template) {
      throw new Error(
        'Cannot prepare draft submission because tasProgramTemplate is missing for the application.'
      );
    }

    if (template.semesterCodeIsVisible) {
      const beginDate = getSingleValueForTasField('COURSES_BEGIN_DATE', application.fields);
      if (beginDate) {
        setTrackedTasFieldValue(
          application,
          'SEMESTER_CODE',
          setSemesterCodeString(String(beginDate))
        );
      }
    }

    const directPayPaymentMethod = template.isDirectPayProgram
      ? application.paymentMethod ||
        getSingleValueForTasField('PAYMENT_TYPE_FOR_DIRECT_PAY', application.fields) ||
        'TAS.AssistanceManner.COURSE_CASH'
      : application.paymentMethod;

    if (template.isDirectPayProgram) {
      setTrackedTasFieldValue(application, 'PAYMENT_TYPE_FOR_DIRECT_PAY', directPayPaymentMethod);
    }

    const typeClassification = application.typeClassification;
    const isCourseCash =
      template.isDirectPayProgram && directPayPaymentMethod === 'TAS.AssistanceManner.COURSE_CASH';

    const usesFinal =
      typeClassification === 'TAS.ProgramType.2' || typeClassification === 'TAS.ProgramType.4.2';

    if (isCourseCash) {
      const courseCashField = usesFinal ? 'CARD_FINAL_APPROVED_TOTAL' : 'CARD_PRE_APPROVED_TOTAL';
      const nonCourseCashField = usesFinal ? 'FINAL_APPROVED_TOTAL' : 'PRE_APPROVED_TOTAL';

      setTrackedTasFieldValue(
        application,
        courseCashField,
        application.requestedCourseCashSubtotalLessScholarships
      );
      setTrackedTasFieldValue(
        application,
        nonCourseCashField,
        application.requestedNonCourseCashSubtotal
      );
    } else {
      setTrackedTasFieldValue(application, 'CARD_PRE_APPROVED_TOTAL', null);
      setTrackedTasFieldValue(application, 'CARD_FINAL_APPROVED_TOTAL', null);

      const approvedField = usesFinal ? 'FINAL_APPROVED_TOTAL' : 'PRE_APPROVED_TOTAL';
      let requestedAmount = application.requestedTotal;

      if (template.calculateTotalByClaimsFinancials) {
        requestedAmount = 0;
      } else if (application.requiresGPA) {
        const gpa = Number(
          getSingleValueForTasField('GRADE_POINT_AVERAGE', application.fields) || 0
        );
        const gpaAmount = this.tuitionAssistance.setTotalBasedOnGPA(gpa);
        requestedAmount = Math.min(gpaAmount, application.requestedTotal);
      }

      setTrackedTasFieldValue(application, approvedField, requestedAmount);
    }

    await saveRecord(this.store, application);
  }

  submitForUserTask = dropTask(async () => {
    this.submitForUserError = null;

    try {
      const application = this.args.application;
      const isDraft = application.state === 'TAS.ApplicationState.DEFINE_COURSES';

      if (isDraft) {
        await this.prepareDraftSubmission(application);

        const employee = application.tasProgramInstance?.employee;
        if (employee) {
          const limitsResult =
            await this.tuitionAssistance.validateAmountRequestedAgainstCurrentLimitsStatus(
              application,
              employee
            );
          if (!limitsResult.can_submit) {
            const reductionAmount = application.isCreditBased
              ? `${limitsResult.reduction_needed} credits`
              : this.intl.formatNumber(limitsResult.reduction_needed / 100, {
                  style: 'currency',
                  currency: 'USD',
                });
            this.submitForUserError = `The requested amount exceeds the employee's remaining benefit. Please reduce by ${reductionAmount}.`;
            return;
          }
        }
      }

      const event = TAS_TRANSITION_EVENT_FOR_METHOD[this.submitForUserAdapterMethod];
      if (!event) {
        throw new Error(
          `No TAS transition event for submitForUserAdapterMethod="${this.submitForUserAdapterMethod}"`
        );
      }
      await this.store.request(
        transitionTasApplicationRequest(
          application.id as string,
          event,
          buildTasActorFromSession(this.sessionContext),
          '',
          buildTasTransitionFieldData(application.fields)
        )
      );

      await reloadRecord(this.store, application);

      this.closeSubmitForUserModal();
    } catch (e) {
      console.error(e);
      // A 422's message is the transport string ("[422 …] PATCH …"); prefer the JSON:API detail.
      this.submitForUserError = parseApiError(e, e instanceof Error ? e.message : String(e));
    }
  });

  rescindStateAction = dropTask(async () => {
    this.rescindError = null;

    try {
      await this.store.request(
        rescindLastApproverActionRequest(this.args.application.id as string)
      );

      await reloadRecord(this.store, this.args.application);

      this.closeRescindModal();
    } catch (e) {
      console.error(e);
      this.rescindError = e instanceof Error ? e.message : String(e);
    }
  });

  /**
   * Requests changes with the entered note, then closes the modal and reloads the application on
   * success.
   */
  confirmRequestChangeTask = dropTask(async () => {
    this.requestChangeError = null;

    try {
      const event = TAS_TRANSITION_EVENT_FOR_METHOD[this.requestChangeAdapterMethod];
      if (!event) {
        throw new Error(
          `No TAS transition event for requestChangeAdapterMethod="${this.requestChangeAdapterMethod}"`
        );
      }
      await this.store.request(
        transitionTasApplicationRequest(
          this.args.application.id as string,
          event,
          buildTasActorFromSession(this.sessionContext),
          this.requestChangeNote,
          buildTasTransitionFieldData(this.args.application.fields)
        )
      );

      this.requestChangeNote = '';
      this.closeRequestChangeModal();

      await reloadRecord(this.store, this.args.application, {
        include: 'tas-assistances,tas-program-instance.employee.tas-participant',
      });
    } catch (e) {
      console.error('Error submitting Request Change:', e);
      this.requestChangeError = e instanceof Error ? e.message : String(e);
    }
  });

  // Track the three non-atomic steps so retries resume rejection, repayment, or hold without repeating committed work.
  #rejectionCommitted = false;
  #repaymentRequested = false;

  confirmRejectionTask = dropTask(async () => {
    this.rejectError = null;

    if (!this.#rejectionCommitted) {
      try {
        const event = TAS_TRANSITION_EVENT_FOR_METHOD[this.rejectionCall];
        if (!event) {
          throw new Error(`No TAS transition event for rejectionCall="${this.rejectionCall}"`);
        }
        await this.store.request(
          transitionTasApplicationRequest(
            this.args.application.id as string,
            event,
            buildTasActorFromSession(this.sessionContext),
            this.rejectNote,
            buildTasTransitionFieldData(this.args.application.fields)
          )
        );
        // Rejection deactivates the card server-side; include it so the financial status refreshes too.
        await reloadRecord(this.store, this.args.application, {
          include: 'pcs-cards.pcs-authorizations',
        });
        this.#rejectionCommitted = true;
      } catch (e) {
        console.error(e);
        this.rejectError = e instanceof Error ? e.message : String(e);
        return;
      }
    }

    // Keep completed-step flags after a follow-up failure; retries must run only unfinished work.
    try {
      if (this.args.application.canRepayOnReject) {
        const participantId = this.args.application.tasProgramInstance.employee.tasParticipant.id;

        if (this.requestRepaymentOnReject && !this.#repaymentRequested) {
          await this.store.request(
            requestRepaymentRequest(
              this.args.application.id as string,
              this.requestedRepaymentAmount,
              this.rejectNote
            )
          );
          await reloadRecord(this.store, this.args.application, {
            include: 'tas-assistances,tas-program-instance.employee.tas-participant',
          });
          // Set only after success so a later retry can still request repayment if the approver newly enables it.
          this.#repaymentRequested = true;
        }

        if (this.holdEmployeeOnReject && this.canHoldOnReject) {
          await this.store.request(
            holdTasParticipantRequest(participantId as string, {
              note: rejectHoldNote(this.args.application.id as string, this.rejectNote),
            })
          );
        }
      }

      this.#rejectionCommitted = false;
      this.#repaymentRequested = false;
      this.rejectNote = '';
      this.closeRejectModal();
      await loadTasApplicationHistory(this.store, this.args.application);
    } catch (e) {
      console.error(e);
      this.rejectError = this.intl.t(
        'tuition_assistance.application_details.reject_committed_followup_failed'
      );
    }
  });

  // Reopen builders depend on the resource family; rescind sends no body, and the caller reloads afterward.
  private async dispatchReopenTransition(): Promise<void> {
    const application = this.args.application;
    const id = application.id as string;
    const actor = buildTasActorFromSession(this.sessionContext);

    if (this.setAdapter === 'tasProgramInstance') {
      switch (this.adapterMethod) {
        case 'requestChanges':
          await this.store.request(declineProgramRequest(id, actor));
          break;
        case 'requestProgramCompletionChanges':
          await this.store.request(requestProgramCompletionChangesRequest(id, actor));
          break;
        case 'requestProgramWithdrawalChanges':
          await this.store.request(requestProgramWithdrawalChangesRequest(id, actor));
          break;
        case 'rescindLastApproverAction':
          await this.store.request(rescindTasProgramInstanceRequest(id));
          return;
        default:
          return;
      }
      await reloadRecord(this.store, application);
      return;
    }

    const fieldData = buildTasTransitionFieldData(application.fields);
    switch (this.adapterMethod) {
      case 'requestChanges':
        await this.store.request(declineCoursesRequest(id, actor, '', fieldData));
        break;
      case 'requestCourseEvidenceChanges':
        await this.store.request(declineEvidenceRequest(id, actor, '', fieldData));
        break;
      case 'rescindLastApproverAction':
        await this.store.request(rescindLastApproverActionRequest(id));
        return;
      default:
        return;
    }
    await reloadRecord(this.store, application);
  }

  reopenApplication = dropTask(async () => {
    this.reopenError = null;

    try {
      await this.dispatchReopenTransition();

      // Only application transitions auto-reject from ATTEND, so restore totals through the application endpoint.
      if (this.lastTransitionLogAutoRejectedFromAttend) {
        await this.store.request(
          updateApprovedTotalRequest(
            this.args.application.id as string,
            this.lastApprovedTotalBeforeRejection,
            'Reset total to amount prior to auto-rejection'
          )
        );
        await reloadRecord(this.store, this.args.application, { include: 'tas-assistances' });
      }

      await reloadRecord(this.store, this.args.application);

      this.closeReopenModal();
    } catch (e) {
      console.error(e);
      this.reopenError = e instanceof Error ? e.message : String(e);
    }
  });

  <template>
    <div
      class="tas-approval-actions sticky bottom-0 left-0 right-0 z-20 tio-bg-surface border-t tio-border-default shadow-lg rounded-b-lg"
      data-test-approval-footer
      ...attributes
    >
      <div class="mx-auto px-2 py-2">
        <div class="tio-col md:flex-row justify-between items-stretch md:items-center">
          <div class="tio-col-tight md:flex-row items-stretch md:items-center">
            <Button
              @intent="primary"
              @size="sm"
              @isRunning={{@approveButtonLoading}}
              @isDisabled={{or (not this.canApproveApplication) this.isDecisionFormOpen}}
              class="md:w-auto w-full"
              {{on "click" this.openApproveModal}}
              data-test-approve-button
            >
              <span class="hidden md:inline">Approve Application</span>
              <span class="md:hidden">Approve</span>
            </Button>

            <Button
              @appearance="outlined"
              @intent="primary"
              @size="sm"
              @isDisabled={{or (not this.canRequestChange) this.isDecisionFormOpen}}
              class="md:w-auto w-full"
              {{on "click" this.openRequestChangeModal}}
              data-test-footer-request-change-button
            >
              Request Change
            </Button>
          </div>

          <div class="tio-row-extra-tight justify-around md:justify-end md:gap-1">
            <ReferenceControls
              class={{if this.referenceControlsOnRight "flex-1 md:hidden" "flex-1 md:flex-none"}}
              @activeDrawer={{@activeDrawer}}
              @onToggle={{@onReferenceToggle}}
              @ai={{@ai}}
              @expenseType={{@expenseType}}
              @hasCourseCash={{@hasCourseCash}}
            />

            {{#unless this.referenceControlsOnRight}}
              <div class="hidden md:block md:mr-16">
                <MoreActionsMenu
                  @placement="top-end"
                  @isOpen={{this.isMoreActionsPopoverOpen}}
                  @onOpen={{this.openMoreActionsPopover}}
                  @onClose={{this.closeMoreActionsPopover}}
                  @isDisabled={{this.isDecisionFormOpen}}
                  @onAction={{this.handleMoreActionsMenu}}
                  @disabledKeys={{this.disabledMenuKeys}}
                  @isRejected={{this.isRejected}}
                  @canSeePolicyOverride={{this.canSeePolicyOverride}}
                  @showInCollections={{this.showInCollections}}
                  @isInCollections={{this.isInCollections}}
                  @canMoveReferenceControls={{this.canMoveReferenceControls}}
                  @referenceControlsOnRight={{false}}
                  data-test-footer-more-actions-button
                />
              </div>
            {{/unless}}

            <Button
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="md:hidden !p-2 !rounded-md hover:tio-bg-brand-soft transition-colors justify-center"
              @isDisabled={{or
                (not (or this.canRejectApplication this.isRejected))
                this.isDecisionFormOpen
              }}
              {{on "click" (if this.isRejected this.openReopenModal this.openRejectModal)}}
              data-test-footer-more-actions-button-mobile
            >
              <PhDotsThree @size="20" class="tio-fg-secondary hover:tio-fg-brand" />
            </Button>
          </div>
        </div>
      </div>
    </div>

    {{#if @onMoveReferenceControls}}
      <nav
        class="tas-approval-rail tio-bg-surface border-l tio-border-default"
        aria-label={{t "common.tas.approval.reference_controls.rail_label"}}
        data-test-approval-reference-rail
      >
        <ReferenceControls
          @vertical={{true}}
          @activeDrawer={{@activeDrawer}}
          @onToggle={{@onReferenceToggle}}
          @ai={{@ai}}
          @expenseType={{@expenseType}}
          @hasCourseCash={{@hasCourseCash}}
        />
        {{#if this.referenceControlsOnRight}}
          <div class="border-t tio-border-default pt-2">
            <MoreActionsMenu
              @placement="left-start"
              @isOpen={{this.isMoreActionsPopoverOpen}}
              @onOpen={{this.openMoreActionsPopover}}
              @onClose={{this.closeMoreActionsPopover}}
              @isDisabled={{this.isDecisionFormOpen}}
              @onAction={{this.handleMoreActionsMenu}}
              @disabledKeys={{this.disabledMenuKeys}}
              @isRejected={{this.isRejected}}
              @canSeePolicyOverride={{this.canSeePolicyOverride}}
              @showInCollections={{this.showInCollections}}
              @isInCollections={{this.isInCollections}}
              @canMoveReferenceControls={{true}}
              @referenceControlsOnRight={{true}}
              data-test-rail-more-actions-button
            />
          </div>
        {{/if}}
      </nav>
    {{/if}}

    <Modal
      @isOpen={{this.isRejectModalOpen}}
      @onClose={{this.closeRejectModal}}
      data-test-reject-modal
      @ariaLabel={{t "common.aria_labels.reject_application"}}
    >
      <:header> {{t "tuition_assistance.application_details.reject_application"}}</:header>
      <:body>
        {{#if @application.canRepayOnReject}}
          <AlertBanner @intent="warning" class="my-4">
            <p data-test-reject-repayment-alert>
              {{t
                "tuition_assistance.application_details.reject_funds_outstanding"
                htmlSafe=true
                courseCash=(t "common.tas.course_cash.default")
              }}
            </p>
          </AlertBanner>

          <Checkbox
            @label={{t "tuition_assistance.application_details.reject_request_repayment"}}
            @checked={{this.requestRepaymentOnReject}}
            @onChange={{this.toggleRequestRepaymentOnReject}}
            class="tio-row-flush tio-text-sm-semibold"
            data-test-reject-request-repayment
          />

          {{#if this.requestRepaymentOnReject}}
            <div class="tio-col mb-4 mt-2">
              {{#if this.showRepaymentAmountOptions}}
                <div class="tio-text-sm-semibold tio-fg-primary">
                  {{t "tuition_assistance.application_details.reject_select_amount"}}
                </div>

                {{#each this.repaymentAmountOptions as |option|}}
                  <SelectableCard
                    @value={{option.key}}
                    @label={{concat option.label ": " (formatAsDollars option.amount)}}
                    @description={{option.description}}
                    @isSelected={{eq this.repaymentAmountSelection option.key}}
                    @onChange={{this.selectRepaymentAmount}}
                  />
                {{/each}}

                <div class="text-center tio-fg-disabled text-sm">
                  {{t "tuition_assistance.application_details.reject_or_custom_amount"}}
                </div>
              {{/if}}

              <Input
                @label={{t "tuition_assistance.application_details.reject_repayment_amount"}}
                @value={{this.requestedRepaymentAmountDisplay}}
                @onChange={{this.updateRequestedRepaymentAmount}}
                @classes={{hash base="tio-text-xs-semibold"}}
                placeholder="$00.00"
                data-test-reject-repayment-amount
                {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2"}}
              />
            </div>
          {{/if}}

          {{#if this.canHoldOnReject}}
            <Checkbox
              @label={{t "tuition_assistance.application_details.reject_hold_employee"}}
              @checked={{this.holdEmployeeOnReject}}
              @onChange={{this.toggleHoldEmployeeOnReject}}
              class="tio-row-flush tio-text-sm-semibold mb-4"
              data-test-reject-hold-employee
            />
          {{/if}}
        {{/if}}
        {{#if this.rejectTextAreaOptions.length}}
          <SelectTextArea
            @options={{this.rejectTextAreaOptions}}
            @optionLabel="title"
            @optionValue="body"
            @value={{this.rejectNote}}
            @onChange={{this.updateRejectNote}}
            @placeholder="Select a response or enter custom feedback..."
            @multiSelect={{true}}
          />
        {{else}}
          <Textarea
            @value={{this.rejectNote}}
            @onChange={{this.updateRejectNote}}
            placeholder="Enter feedback for the employee..."
            data-test-reject-note
          />
        {{/if}}
        {{#if this.rejectError}}
          <ErrorMessages
            @showErrors={{true}}
            @error={{this.rejectError}}
            class="tio-row-tight text-sm"
          />
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.confirmRejectionTask.isRunning}}
          @submitText="Submit"
          @onSubmit={{this.confirmRejectionTask.perform}}
          @onCancel={{this.closeRejectModal}}
          @submitDisabled={{not this.isRejectFormValid}}
        />
      </:footer>
    </Modal>

    <Modal
      @isOpen={{this.submitForUserModalOpen}}
      @onClose={{this.closeSubmitForUserModal}}
      data-test-submit-for-user-modal
      @ariaLabel={{t "common.aria_labels.submit_application_for_user"}}
    >
      <:header>
        {{t "tuition_assistance.application_approval.submit_for_user.title"}}
      </:header>
      <:body>
        <div class="tio-col-loose">
          <p class="text-sm tio-fg-secondary">
            {{t "tuition_assistance.application_approval.submit_for_user.description"}}
          </p>

          {{#if
            (and
              (eq @application.state "TAS.ApplicationState.DEFINE_COURSES")
              @application.tasProgramInstance.tasProgramTemplate.isDirectPayProgram
            )
          }}
            <AlertBanner @intent="info">
              {{#if this.isCourseCashPayment}}
                {{t "tuition_assistance.application_approval.submit_for_user.course_cash_info"}}
              {{else}}
                {{t "tuition_assistance.application_approval.submit_for_user.reimbursement_info"}}
              {{/if}}
            </AlertBanner>

            {{#if this.isDraftCourseCashWithoutCardholder}}
              <AlertBanner @intent="warning">
                {{t "tuition_assistance.application_approval.submit_for_user.missing_cardholder"}}
              </AlertBanner>
            {{/if}}
          {{/if}}

          {{#if this.missingRequirementsForSubmitForUser.length}}
            <ErrorMessages
              @showErrors={{true}}
              @error={{this.missingRecsAlertForSubmitForUser}}
              class="tio-row-tight text-sm"
            />
          {{/if}}

          {{#if this.submitForUserError}}
            <ErrorMessages
              @showErrors={{true}}
              @error={{this.submitForUserError}}
              class="tio-row-tight text-sm"
            />
          {{/if}}
        </div>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.submitForUserTask.isRunning}}
          @submitText={{t "common.submit"}}
          @cancelText={{t "common.cancel"}}
          @onSubmit={{this.submitForUserTask.perform}}
          @onCancel={{this.closeSubmitForUserModal}}
          @submitDisabled={{or
            (gt this.missingRequirementsForSubmitForUser.length 0)
            this.isDraftCourseCashWithoutCardholder
          }}
        />
      </:footer>
    </Modal>

    <Modal
      @isOpen={{this.isRescindModalOpen}}
      @onClose={{this.closeRescindModal}}
      data-test-rescind-modal
      @ariaLabel={{t "common.aria_labels.rescind"}}
    >
      <:header>
        Rescind
      </:header>
      <:body>
        <p class="tio-text-heading tio-fg-primary">
          {{t "tuition_assistance.application_details.rescind.body"}}
        </p>
        <p class="my-4 tio-fg-primary">
          {{t "tuition_assistance.application_details.rescind.footnote"}}
        </p>

        {{#if this.rescindError}}
          <ErrorMessages
            @showErrors={{true}}
            @error={{this.rescindError}}
            class="tio-row-tight text-sm"
          />
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.rescindStateAction.isRunning}}
          @submitText="Confirm"
          @onSubmit={{this.rescindStateAction.perform}}
          @onCancel={{this.closeRescindModal}}
        />
      </:footer>
    </Modal>

    <Modal
      @isOpen={{this.isRequestChangeModalOpen}}
      @onClose={{this.closeRequestChangeModal}}
      @nonModal={{@nonModalDecisions}}
      data-test-request-change-modal
      @ariaLabel={{t "common.aria_labels.request_change"}}
    >
      <:header>
        Request Change
      </:header>
      <:body>
        <p class="text-sm tio-fg-secondary mb-4">
          Send this application back to the employee with feedback for revision.
        </p>

        {{#if this.requestChangeTextAreaOptions.length}}
          <SelectTextArea
            @options={{this.requestChangeTextAreaOptions}}
            @optionLabel="title"
            @optionValue="body"
            @value={{this.requestChangeNote}}
            @onChange={{this.updateRequestChangeNote}}
            @placeholder="Select a response or enter custom feedback..."
            @multiSelect={{true}}
          />
        {{else}}
          <Textarea
            @value={{this.requestChangeNote}}
            @onChange={{this.updateRequestChangeNote}}
            placeholder="Enter feedback for the employee..."
            data-test-request-change-note
            class="min-h-32"
          />
        {{/if}}

        {{#unless this.requestChangeNote}}
          <p class="text-sm tio-fg-danger mt-2">
            A comment is required before submitting.
          </p>
        {{/unless}}

        {{#if this.requestChangeError}}
          <ErrorMessages
            @showErrors={{true}}
            @error={{this.requestChangeError}}
            class="tio-row-tight text-sm mt-2"
          />
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.confirmRequestChangeTask.isRunning}}
          @submitText="Submit"
          @onSubmit={{this.confirmRequestChangeTask.perform}}
          @onCancel={{this.closeRequestChangeModal}}
          @submitDisabled={{not this.requestChangeNote}}
        />
      </:footer>
    </Modal>

    <Modal
      @isOpen={{this.isReopenModalOpen}}
      @onClose={{this.closeReopenModal}}
      @size="md"
      data-test-reopen-modal
      @ariaLabel={{t "common.aria_labels.reopen_application"}}
    >
      <:header>
        Reopen Application
      </:header>
      <:body>
        <p class="tio-fg-secondary mb-4">
          Are you sure you want to reopen this rejected application? This will return the
          application to a pending state for review.
        </p>

        {{#if this.reopenError}}
          <ErrorMessages @showErrors={{true}} @error={{this.reopenError}} class="mt-4" />
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.reopenApplication.isRunning}}
          @submitText="Reopen Application"
          @cancelText="Cancel"
          @onSubmit={{this.reopenApplication.perform}}
          @onCancel={{this.closeReopenModal}}
          @submitIntent="primary"
          @submitDisabled={{this.reopenApplication.isRunning}}
        />
      </:footer>
    </Modal>

    {{! Approve Modal - Unmount/remount to reset state }}
    {{#if this.isApproveModalOpen}}
      <ApproveModal
        @show={{this.isApproveModalOpen}}
        @onClose={{this.closeApproveModal}}
        @nonModal={{@nonModalDecisions}}
        @application={{@application}}
        @draftComment={{this.approveCommentDraft}}
        @onDraftCommentChange={{this.updateApproveCommentDraft}}
      />
    {{/if}}

    <Modal
      @isOpen={{this.isOverrideModalOpen}}
      @onClose={{this.closeOverrideModal}}
      data-test-policy-override-modal
      @ariaLabel={{t "common.aria_labels.override_policies"}}
    >
      <:header>
        {{t "common.tuition_assistance.application_details.policy_override_confirm"}}
      </:header>
      <:body>
        <p class="tio-fg-secondary mb-4">
          {{t "common.tuition_assistance.application_details.policy_override_description"}}
        </p>
        <p class="font-semibold italic tio-fg-secondary">
          {{t "common.tuition_assistance.application_details.policy_override_warning"}}
        </p>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.confirmPolicyOverrideTask.isRunning}}
          @onCancel={{this.closeOverrideModal}}
          @onSubmit={{this.confirmPolicyOverrideTask.perform}}
          @submitText={{t "common.confirm"}}
          @submitIntent="danger"
        />
      </:footer>
    </Modal>

    <Modal
      @isOpen={{this.isInCollectionsModalOpen}}
      @onClose={{this.closeInCollectionsModal}}
      data-test-in-collections-modal
      @ariaLabel={{if
        this.isInCollections
        (t "tuition_assistance.application_details.in_collections_toggle.remove_title")
        (t "tuition_assistance.application_details.in_collections_toggle.mark_title")
      }}
    >
      <:header>
        {{if
          this.isInCollections
          (t "tuition_assistance.application_details.in_collections_toggle.remove_title")
          (t "tuition_assistance.application_details.in_collections_toggle.mark_title")
        }}
      </:header>
      <:body>
        <p class="tio-fg-secondary mb-4">
          {{if
            this.isInCollections
            (t "tuition_assistance.application_details.in_collections_toggle.remove_body")
            (t "tuition_assistance.application_details.in_collections_toggle.mark_body")
          }}
        </p>

        {{#if this.inCollectionsError}}
          <ErrorMessages
            @showErrors={{true}}
            @error={{this.inCollectionsError}}
            class="tio-row-tight text-sm"
          />
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.toggleInCollectionsTask.isRunning}}
          @onCancel={{this.closeInCollectionsModal}}
          @onSubmit={{this.toggleInCollectionsTask.perform}}
          @submitText={{t "common.confirm"}}
        />
      </:footer>
    </Modal>
  </template>
}

export default ApprovalFooter;
